import { ObjectId } from "bson";
import { ChatbotConfig, HistoryItemContext, HistoryItemResponse, MID, ServerChatbotConfig } from "./index.js";

export const TOOL_ID_SCHNITTSTELLE = "schnittstelle";
export const TOOL_ID_WEBSITESEARCH = "websitesearch";
export const TOOL_ID_WEBSITESCRAPE = "websitescrape";
export const TOOL_ID_DATATRUNK = "datatrunk";
export const TOOL_ID_MCP = "mcp";
export const TOOL_ID_CUSTOMTOOLKIT = "customtoolkit";
export const TOOL_ID_FILESEARCH = "filesearch";
export const TOOL_ID_WEBHOOK = "webhook";
export const TOOL_ID_USER_DOCUMENTS = "userdocuments";

export const toolConfigToolArr = [TOOL_ID_WEBSITESEARCH, TOOL_ID_SCHNITTSTELLE, TOOL_ID_WEBSITESCRAPE, TOOL_ID_DATATRUNK, TOOL_ID_MCP, TOOL_ID_CUSTOMTOOLKIT, TOOL_ID_FILESEARCH, TOOL_ID_WEBHOOK] as const;
export type ToolConfigTool = typeof toolConfigToolArr[number];

export const CustomToolKitTypeArr = ["js"] as const;
export type CustomToolKitTypeItem = typeof CustomToolKitTypeArr[number];

export type ToolConfig = GeneralToolConfig | ToolConfigWebsiteSearch | ToolConfigCustomToolKit;



export type ToolConfigParam = {
  description: string,
  required: boolean,
  type: "string" | "number" | "boolean",
  key: string
}

export type ToolConfigEnumParam = {
  description: string,
  required: boolean,
  type: "enum",
  key: string,
  values: string[]
}

export type SkillState<ID extends MID = string> = {
  skillId: string,
  enabled: boolean,
  forced: boolean,
  updated: Date,
  usages: number,
  context: HistoryItemContext,
  tool: ToolConfigTool,
  toolId: string,
  _id: ID
}

export type GeneralToolConfig = {
  tool: ToolConfigTool
  id: string,
  skillId: string; // must not be unique, user can edit it
  description: string,
  enabled: boolean,
  params: ToolConfigParam[]
}

export type ToolConfigMCPTool<ID extends MID = string> = {
  _id: ID,
  tool: typeof TOOL_ID_MCP,
  skillId: string,
  enabled: boolean
}

export type ToolConfigWebsiteSearch = GeneralToolConfig & {
  tool: typeof TOOL_ID_WEBSITESEARCH,
  url: string,
  name: string
}

export type ToolConfigSchnittstelle = GeneralToolConfig & {
  tool: typeof TOOL_ID_SCHNITTSTELLE,
  url: string,
  name: string
}

export type ToolConfigWebhook = GeneralToolConfig & {
  tool: typeof TOOL_ID_WEBHOOK,
  url: string,
  name: string,
  authMethod: 'none' | 'basic',
  authData: WebhookBasicAuto,
  urlMethod: 'get' | 'post'
  contentType?: "application/json" | "application/x-www-form-urlencoded"
}

export type CustomToolKitParam = ToolConfigParam | ToolConfigEnumParam;

export type WebhookBasicAuto = {
  user: string,
  password: string
}

export const CustomToolKitAnswerModeArr = ["sequence", "sequenceloop", "random"] as const
export type CustomToolKitAnswerMode = typeof CustomToolKitAnswerModeArr[number];

export type ToolConfigCustomToolKit = Omit<GeneralToolConfig, "params"> & {
  tool: typeof TOOL_ID_CUSTOMTOOLKIT,
  name: string,
  type: CustomToolKitTypeItem,
  content: string,
  params: CustomToolKitParam[],
  toolCallAnswer: string[]
  answerMode: CustomToolKitAnswerMode,
  detectedErrors: boolean
}

export const isCustomToolKitToolConf = (toolConfig: ToolConfig): toolConfig is ToolConfigCustomToolKit => {
  return (toolConfig as ToolConfig).tool === TOOL_ID_CUSTOMTOOLKIT;
}
export const isToolConfigEnumParam = (p: ToolConfigParam | ToolConfigEnumParam): p is ToolConfigEnumParam => {
  return (p as ToolConfigEnumParam).type === "enum";
}
export const isToolConfigParam = (p: ToolConfigParam | ToolConfigEnumParam): p is ToolConfigParam => {
  return (p as ToolConfigParam).type === "string" || (p as ToolConfigParam).type === "number" || (p as ToolConfigParam).type === "boolean";
}
export const getCustomToolKitVarsHeader = <ID extends MID = string>(toolConfig: ToolConfigCustomToolKit, botConfig: ChatbotConfig | ServerChatbotConfig, keyValObject: any | null, triggerHistoryItem: HistoryItemResponse<ID>, skillStates?: SkillState[] | SkillState<ObjectId>[]): string => {
  if (keyValObject == null) {
    keyValObject = getCustomKitKeyValDummy(toolConfig);
  }


  const confLines: string[] = [
    `  "skillId" : "${toolConfig.skillId}"`,
    `  "botId" : "${botConfig._id}"`,
    `  "botColor" : "${botConfig.color}"`,
    `  "botName" : "${botConfig.name.split('"').join('\\"')}"`,
    `  "botIcon" : "${botConfig.icon}"`,
    `  "templateVars" : ${JSON.stringify(botConfig.templateVars ?? [])}`,
    //`  "triggerHistoryItemId" : "${triggerHistoryItem._id}"`,
    //`  "triggerHistoryItemContent" : "${JSON.stringify(triggerHistoryItem.content).split('"').join('\\"')}"`,
    //TODO vielleicht diese ganzen lines loswerden

    //`  "triggerHistoryItemOpenAiMessageId" : "${triggerHistoryItem._id}"`,
    //`  "triggerHistoryItemOpenAiThreadId" : "${triggerHistoryItem.threadId}"`,


  ];

  const skillLines: string[] = [];

  if (skillStates != null) {
    const skillstatesWithSkillId = skillStates.filter(tc => tc.skillId != "");
    for (const ss of skillstatesWithSkillId) {
      skillLines.push(`  "${ss.skillId}" : ${ss.enabled}`);
      skillLines.push(`  "${ss.skillId}_count" : ${ss.usages}`);

    }
  } else {
    const toolConfigWithSkillId = botConfig.toolConfigs.filter(tc => tc.skillId != "");
    for (const tc of toolConfigWithSkillId) {
      skillLines.push(`  "${tc.skillId}" : ${tc.enabled}`);
      skillLines.push(`  "${tc.skillId}_count" : 0`);
    }
  }

  const varLines: string[] = [];
  for (const param of toolConfig.params) {
    let val = keyValObject[param.key];
    if (val == null || param.type == "boolean" || param.type == "number") {
      varLines.push(`  "${param.key}" : ${val}`);
    } else {
      varLines.push(`  "${param.key}" : \`${val.split('`').join("'").split('"').join('\"')}\``);
    }
  }
  return `
let conf={
${confLines.join(",\n")}
};\n\n
let skill={
${skillLines.join(",\n")}
};\n\n
let vars = {
${varLines.join(",\n")}
};\n\n`;
}

export const getCustomKitKeyValDummy = (toolConfig: ToolConfigCustomToolKit) => {
  const keyValObject: any = {};
  for (const param of toolConfig.params) {
    if (param.type == "boolean") {
      keyValObject[param.key] = true;
    } else if (param.type == "number") {
      keyValObject[param.key] = 0;
    } else if (isToolConfigEnumParam(param)) {
      let paramValues: string = "";
      for (const value of param.values) {
        paramValues += '"' + value + '", ';
      }
      paramValues = paramValues.slice(0, -2);
      keyValObject[param.key] = `[${paramValues}]`;
    } else {
      keyValObject[param.key] = "Some Value for: " + param.key;
    }
  }
  return keyValObject;
}
